import { z } from "zod";
import { hybridClockSchema } from "./clock";
import { currencySchema, moneySchema } from "./money";

const timestamp = z.number().int().nonnegative();
const id = z.string().min(1).max(128);
const minor = z.number().int().nonnegative();

/** Fields every synced entity carries. */
const entityBase = {
  id,
  createdAt: timestamp,
  updatedAt: timestamp,
  /** Moved to Trash. Restorable for 30 days. */
  deletedAt: timestamp.nullable(),
  /** Removed by Undo right after creation; never shown in Trash. */
  discardedAt: timestamp.nullable(),
  fieldClocks: z.record(z.string(), hybridClockSchema)
};

export const billingModeSchema = z.enum(["hourly", "fixed", "non-billable"]);
export type BillingMode = z.infer<typeof billingModeSchema>;
export const invoiceLanguageSchema = z.enum(["en", "es"]);
export type InvoiceLanguage = z.infer<typeof invoiceLanguageSchema>;
export const roundingMinutesSchema = z.union([z.literal(6), z.literal(15), z.literal(30)]).nullable();
export const themeSchema = z.enum(["light", "dark", "system"]);
export type Theme = z.infer<typeof themeSchema>;
/** Days after the issue date an Invoice is due: Net 0, 7, 15, or 30. */
export const paymentTermSchema = z.union([z.literal(0), z.literal(7), z.literal(15), z.literal(30)]);
export type PaymentTerm = z.infer<typeof paymentTermSchema>;
/** Percentages are stored in basis points: 1600 is 16%. */
const basisPoints = z.number().int().min(0).max(10_000);

/** A named tax applied to an Invoice's subtotal after discount, e.g. "IVA" at 16%. */
export const taxLineSchema = z.object({ id, name: z.string().max(60), rate: basisPoints });
export type TaxLine = z.infer<typeof taxLineSchema>;

export const billingDetailsSchema = z.object({
  name: z.string().max(200),
  address: z.string().max(1000),
  email: z.string().max(320),
  taxId: z.string().max(100)
});
export type BillingDetails = z.infer<typeof billingDetailsSchema>;

/** Largest Logo, as a data URL. It is copied onto every Sent Invoice, so it stays small. */
export const MAX_LOGO_LENGTH = 150_000;
/** Largest Client Picture, as a data URL. It shows at most 44px wide and syncs with the Client. */
export const MAX_CLIENT_PICTURE_LENGTH = 60_000;
const pictureSchema = (max: number) => z.string().max(max).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/).nullable().default(null);
/** The Business Profile's Logo as a PNG, JPEG, or WebP data URL; null prints no logo. */
export const logoSchema = pictureSchema(MAX_LOGO_LENGTH);

export const businessProfileSchema = billingDetailsSchema.extend({
  /** Defaults keep Business Profiles stored before Logos existed readable. */
  logo: logoSchema,
  /** Payment instructions keyed by Currency, plus an optional "default". */
  paymentInstructions: z.record(z.string(), z.string().max(2000))
});
export type BusinessProfile = z.infer<typeof businessProfileSchema>;

export const settingsSchema = z.object({
  ...entityBase,
  defaultCurrency: currencySchema,
  /** Minor units per hour, in the Default Currency. */
  defaultHourlyRate: minor.nullable(),
  /** Monthly Goal per Currency, in minor units. */
  monthlyGoals: z.record(currencySchema, minor),
  theme: themeSchema,
  invoicePrefix: z.string().max(20),
  businessProfile: businessProfileSchema,
  /** Defaults keep Settings stored before the Calendar existed readable. */
  hideWeekends: z.boolean().default(false),
  /**
   * Google calendars switched on or off, by calendar id. A calendar not listed shows only when it
   * is the primary one (ADR 0027).
   */
  googleCalendars: z.record(z.string().max(1024), z.boolean()).default({}),
  /** Where new Events go: null for ontrack, or the id of a Google calendar. */
  newEventsCalendar: z.string().max(1024).nullable().default(null),
  /**
   * Project remembered per Google event series that ontrack can't write to (an invitation), keyed
   * by calendar and series. Events ontrack can write to keep their Project on the event itself.
   */
  googleProjects: z.record(z.string().max(2100), id).default({})
});
export type Settings = z.infer<typeof settingsSchema>;

export const clientSchema = z.object({
  ...entityBase,
  name: z.string().min(1).max(200),
  defaultCurrency: currencySchema.nullable(),
  /** Minor units per hour, in the Client's currency. */
  defaultHourlyRate: minor.nullable(),
  invoiceLanguage: invoiceLanguageSchema,
  billingDetails: billingDetailsSchema,
  /** A logo or photo shown beside the Client's name; null shows initials. Never printed on Invoices. */
  picture: pictureSchema(MAX_CLIENT_PICTURE_LENGTH),
  roundingMinutes: roundingMinutesSchema,
  /** Defaults keep Clients stored before invoicing existed readable. */
  paymentTermDays: paymentTermSchema.default(15),
  /** Tax lines copied onto each new Invoice for this Client. */
  defaultTaxes: z.array(taxLineSchema).max(10).default([]),
  archivedAt: timestamp.nullable()
});
export type Client = z.infer<typeof clientSchema>;

/** The muted palette Projects are drawn in. Ember is never one of them: it stays the live signal. */
export const PROJECT_COLORS = ["rose", "plum", "violet", "indigo", "blue", "sky", "teal", "sage", "moss", "olive", "gold", "slate"] as const;
export const projectColorSchema = z.enum(PROJECT_COLORS);
export type ProjectColor = z.infer<typeof projectColorSchema>;

export const projectSchema = z.object({
  ...entityBase,
  name: z.string().min(1).max(200),
  /** Null means a colour derived from the id, so older Projects get one without a migration. */
  color: projectColorSchema.nullable().default(null),
  clientId: id.nullable(),
  billingMode: billingModeSchema,
  /** Overrides the Client and Default Currency when set. */
  currency: currencySchema.nullable(),
  /** Minor units per hour, in the Project's currency. Overrides Client and default rates. */
  hourlyRate: minor.nullable(),
  /** Project-level Fixed Price, in the Project's currency. */
  fixedPrice: minor.nullable(),
  /** Deadline: the local day the Project is due, YYYY-MM-DD. Defaults keep older Projects readable. */
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  /** The Delivery Event planned for the Deadline, which moves with it. */
  deadlineEventId: id.nullable().default(null),
  completedAt: timestamp.nullable(),
  archivedAt: timestamp.nullable()
});
export type Project = z.infer<typeof projectSchema>;

export const taskSchema = z.object({
  ...entityBase,
  projectId: id,
  /** Set on a sub-task: the Task it sits under, in the same Project. Sub-tasks never nest. */
  parentId: id.nullable().default(null),
  name: z.string().min(1).max(200),
  doneAt: timestamp.nullable(),
  estimateMinutes: z.number().int().positive().nullable(),
  /** Milestone Fixed Price, in the Project's currency. */
  fixedPrice: minor.nullable(),
  archivedAt: timestamp.nullable()
});
export type Task = z.infer<typeof taskSchema>;

export const timeEntrySchema = z.object({
  ...entityBase,
  projectId: id.nullable(),
  taskId: id.nullable(),
  description: z.string().max(1000),
  start: timestamp,
  /** Null while the entry is the running Timer. */
  end: timestamp.nullable(),
  billable: z.boolean(),
  /** Hourly Rate copied when the entry was recorded (minor units per hour). */
  rateSnapshot: moneySchema.nullable(),
  invoiceId: id.nullable(),
  /** The planned Event (or ICS event, "ics:<feed>:<uid>") this time was logged from. */
  eventId: z.string().max(400).nullable().default(null),
  /** Which occurrence, by its original start, when the Event repeats. */
  eventOccurrence: timestamp.nullable().default(null)
}).refine((entry) => entry.end === null || entry.end >= entry.start, { message: "An entry cannot end before it starts", path: ["end"] });
export type TimeEntry = z.infer<typeof timeEntrySchema>;

/** How an Event repeats: a subset of RRULE (FREQ, INTERVAL, BYDAY, UNTIL, COUNT). */
export const repeatRuleSchema = z.object({
  freq: z.enum(["daily", "weekly", "monthly"]),
  interval: z.number().int().min(1).max(99),
  /** Weekly only. JavaScript weekdays, 0 is Sunday. Empty means the first occurrence's weekday. */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7),
  /** Last day an occurrence may start on, YYYY-MM-DD, inclusive. */
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  /** Number of occurrences in total. */
  count: z.number().int().min(1).max(999).nullable()
});
export type RepeatRule = z.infer<typeof repeatRuleSchema>;

/**
 * A planned block on the Calendar. It never counts as tracked time or money. Timed Events use
 * `start`/`end`; all-day Events use local dates so they stay on the same days in any time zone.
 */
export const eventSchema = z.object({
  ...entityBase,
  title: z.string().max(200),
  notes: z.string().max(4000),
  projectId: id.nullable(),
  taskId: id.nullable(),
  allDay: z.boolean(),
  start: timestamp,
  end: timestamp,
  /** All-day only: first and last day, YYYY-MM-DD, both included. */
  startDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  endDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  repeat: repeatRuleSchema.nullable(),
  /** Original starts of occurrences removed from a repeating Event. */
  exdates: z.array(timestamp).max(1000),
  /** Set on an Event that replaces one occurrence of a repeating Event. */
  seriesId: id.nullable(),
  occurrenceStart: timestamp.nullable()
}).refine((event) => event.end >= event.start, { message: "An event cannot end before it starts", path: ["end"] })
  .refine((event) => !event.allDay || (event.startDay !== null && event.endDay !== null && event.endDay >= event.startDay), { message: "All-day events need their days", path: ["startDay"] });
export type CalendarEvent = z.infer<typeof eventSchema>;

export const invoiceStatusSchema = z.enum(["draft", "sent", "paid"]);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;
export const invoiceGroupingSchema = z.enum(["entry", "task", "project"]);
export type InvoiceGrouping = z.infer<typeof invoiceGroupingSchema>;
export const fixedItemRefSchema = z.object({ type: z.enum(["task", "project"]), id });
export type FixedItemRef = z.infer<typeof fixedItemRefSchema>;

/** A discount on the subtotal: a percentage in basis points, or an amount in minor units. */
export const discountSchema = z.object({ type: z.enum(["percent", "amount"]), value: minor });
export type Discount = z.infer<typeof discountSchema>;

export const manualLineSchema = z.object({
  id,
  description: z.string().max(500),
  quantity: z.number().nonnegative().max(1_000_000),
  /** Minor units, in the Invoice's currency. */
  unitPrice: minor
});
export type ManualLine = z.infer<typeof manualLineSchema>;

/**
 * A Fixed Price item on an Invoice: a Deposit (a percentage invoiced early) or the item's balance
 * (its price minus what other Invoices already billed for it).
 */
export const fixedLineSchema = z.object({ id, item: fixedItemRefSchema, kind: z.enum(["deposit", "balance"]), percent: basisPoints });
export type FixedLine = z.infer<typeof fixedLineSchema>;

/** Most Installments a Draft can be split into. */
export const MAX_INSTALLMENTS = 5;

/**
 * Where an Invoice sits in a split: its group (the id of Installment 1, which holds the lines,
 * entries, discount, taxes, and Reference), its place in the order, and its share in basis points.
 */
export const installmentSchema = z.object({ groupId: id, index: z.number().int().min(1).max(MAX_INSTALLMENTS), percent: basisPoints });
export type Installment = z.infer<typeof installmentSchema>;

/** One computed row of an Invoice. Language-free: the document adds the words. */
export const invoiceLineSchema = z.object({
  key: z.string().max(400),
  /** hours: grouped time; fixed: a whole Fixed Price; balance: what is left after Deposits. */
  kind: z.enum(["hours", "deposit", "balance", "fixed", "manual"]),
  /** Null means time without a Task ("General"). */
  title: z.string().max(500).nullable(),
  context: z.string().max(500).nullable(),
  /** Day of the work, for lines that are a single entry. */
  date: timestamp.nullable(),
  notes: z.array(z.string().max(1000)).max(50),
  quantity: z.number().nonnegative(),
  unitPrice: z.number().int(),
  amount: z.number().int(),
  item: fixedItemRefSchema.nullable(),
  percent: basisPoints.nullable()
});
export type InvoiceLine = z.infer<typeof invoiceLineSchema>;

/** What a Sent Invoice froze: its lines and both parties exactly as they were sent. */
export const invoiceSnapshotSchema = z.object({
  language: invoiceLanguageSchema,
  issuer: billingDetailsSchema.extend({ logo: logoSchema }),
  recipient: billingDetailsSchema,
  paymentInstructions: z.string().max(2000),
  lines: z.array(invoiceLineSchema).max(2000)
});
export type InvoiceSnapshot = z.infer<typeof invoiceSnapshotSchema>;

export const invoiceSchema = z.object({
  ...entityBase,
  clientId: id,
  currency: currencySchema,
  status: invoiceStatusSchema,
  /** Assigned when first marked Sent, then kept for good. */
  number: z.string().max(60).nullable(),
  numberYear: z.number().int().nullable(),
  sequence: z.number().int().positive().nullable(),
  /** Local midnight of the first and last day of work billed. */
  periodFrom: timestamp,
  periodTo: timestamp,
  grouping: invoiceGroupingSchema,
  paymentTermDays: paymentTermSchema,
  /** Local midnight. Issue and due dates are set when Sent; paid date when Paid. */
  issueDate: timestamp.nullable(),
  dueDate: timestamp.nullable(),
  paidAt: timestamp.nullable(),
  fixedLines: z.array(fixedLineSchema).max(200),
  manualLines: z.array(manualLineSchema).max(200),
  discount: discountSchema.nullable(),
  taxes: z.array(taxLineSchema).max(10),
  /** A PO or contract number, shown under the Invoice Number. */
  reference: z.string().max(100),
  note: z.string().max(2000),
  /** Null while Draft: a Draft is always computed from the current data. */
  snapshot: invoiceSnapshotSchema.nullable(),
  /** Null unless the Invoice is one Installment of a split. Defaults keep older Invoices readable. */
  installment: installmentSchema.nullable().default(null)
});
export type Invoice = z.infer<typeof invoiceSchema>;

export const entityTypeSchema = z.enum(["settings", "client", "project", "task", "timeEntry", "invoice", "event"]);
export type EntityType = z.infer<typeof entityTypeSchema>;

export interface EntityMap {
  settings: Settings;
  client: Client;
  project: Project;
  task: Task;
  timeEntry: TimeEntry;
  invoice: Invoice;
  event: CalendarEvent;
}

export const entitySchemas = {
  settings: settingsSchema,
  client: clientSchema,
  project: projectSchema,
  task: taskSchema,
  timeEntry: timeEntrySchema,
  invoice: invoiceSchema,
  event: eventSchema
} as const;

export const SETTINGS_ID = "settings";

/** Defaults applied when an entity is created from a partial patch. */
export function entityDefaults(type: EntityType): Record<string, unknown> {
  const base = { deletedAt: null, discardedAt: null };
  switch (type) {
    case "settings":
      return {
        ...base,
        defaultCurrency: "USD",
        defaultHourlyRate: null,
        monthlyGoals: {},
        theme: "system",
        invoicePrefix: "",
        businessProfile: { name: "", address: "", email: "", taxId: "", logo: null, paymentInstructions: {} },
        hideWeekends: false,
        googleCalendars: {},
        newEventsCalendar: null,
        googleProjects: {}
      };
    case "client":
      return { ...base, defaultCurrency: null, defaultHourlyRate: null, invoiceLanguage: "en", billingDetails: { name: "", address: "", email: "", taxId: "" }, picture: null, roundingMinutes: null, paymentTermDays: 15, defaultTaxes: [], archivedAt: null };
    case "project":
      return { ...base, clientId: null, color: null, billingMode: "hourly", currency: null, hourlyRate: null, fixedPrice: null, deadline: null, deadlineEventId: null, completedAt: null, archivedAt: null };
    case "task":
      return { ...base, parentId: null, doneAt: null, estimateMinutes: null, fixedPrice: null, archivedAt: null };
    case "timeEntry":
      return { ...base, projectId: null, taskId: null, description: "", end: null, billable: true, rateSnapshot: null, invoiceId: null, eventId: null, eventOccurrence: null };
    case "invoice":
      return {
        ...base, status: "draft", number: null, numberYear: null, sequence: null, grouping: "task", paymentTermDays: 15, issueDate: null, dueDate: null,
        paidAt: null, fixedLines: [], manualLines: [], discount: null, taxes: [], reference: "", note: "", snapshot: null, installment: null
      };
    case "event":
      return { ...base, title: "", notes: "", projectId: null, taskId: null, allDay: false, startDay: null, endDay: null, repeat: null, exdates: [], seriesId: null, occurrenceStart: null };
  }
}
